import React, { useEffect, useState } from "react";
import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import { getAllProducts } from "../../services/productServices";
import axiosClient from "../../services/axiosClient";
import SectionDivider from "../../components/SectionDivider";
import { useParams, useSearchParams } from "react-router-dom";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import CollapsibleCard from "../attendance-system/CollapsibleCard";
import CustomInput from "../../components/CustomInput";
import useDebounce from "../../utils/useDebounce";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import editIcon from "../../assets/edit_icon.png";
import CustomButton from "../../components/CustomButton";
import deleteIcon from "../../assets/delete_Icon.png";
import Modal from "../../components/Modal";
import saveIcon from "../../assets/save_icon.png";
import ErrorBox from "../../components/ErrorBox";
import closeIcon from "../../assets/close_icon.png";
import {
  CollapsibleContent,
  CollapsibleGroup,
  CollapsibleHeader,
  CollapsibleLabel,
  CollapsibleList,
} from "../../components/CollapsibleGroup";
import { formatDate } from "../../utils/toDate";
import InformationBox from "../../components/InformationBox";
import AreYouSureModal from "../../components/AreYouSureModal";

const DeliveredSuppliesItemPage = () => {
  const { t } = useTranslation("general");
  const [searchParams] = useSearchParams();
  const [supple, setSupple] = useState();

  return (
    <PageWrapper withBackButton title={t("supplies")}>
      <div className="o-column o-row--gap-size_300 u-margin--top_500 u-margin--bottom_600">
        <div className="o-row o-row--gap-size_200">
          {t("supplieName")} :<span>{supple?.name}</span>
        </div>
      </div>
      <SectionDivider title={t("suppliesAccordingToClass")} />
      {searchParams.get("type") === "book" ? (
        <BooksClassItem setSupple={setSupple} supple={supple} />
      ) : (
        <UniformsClassItem setSupple={setSupple} supple={supple} />
      )}
    </PageWrapper>
  );
};

function BooksClassItem({ setSupple, supple }) {
  const { t } = useTranslation("general");
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const [loadingBooks, setLoadingBooks] = useState(true);
  const [booksData, setBooksData] = useState([]);
  const [errorGettingBooks, setErrorGettingBooks] = useState("");

  const getAllBooks = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingBooks(true);
      const classId = searchParams.get("classId");
      const response = await axiosClient.get(
        "/studentSubClass/getAllForProduct",
        {
          params: {
            classId: classId,
            productId: id,
          },
        }
      );

      if (response.data?.classes) {
        setSupple({
          schoolClass: { ...response.data.classes },
          product: { ...response.data.product },
          name: response.data.product.name,
        });
        setBooksData(response.data.classes);
      }
    } catch (err) {
      console.error("Failed to fetch books:", err);
      setErrorGettingBooks("gettingSupplieFailed");
    } finally {
      setLoadingBooks(false);
    }
  };

  useEffect(() => {
    getAllBooks();
  }, []);


  if (loadingBooks) return <Loader size={LOADER_SIZES.MEDIUM} />;

  if (!loadingBooks && !errorGettingBooks && booksData?.length === 0)
    return <InformationBox>{t("noDeliveredSupplesFound")}</InformationBox>;

  if (!loadingBooks && errorGettingBooks)
    return <ErrorBox>{t(errorGettingBooks)}</ErrorBox>;

  return (
    <div className="o-column o-row--gap-size_500">
      <CollapsibleList>
        {booksData?.map((schoolClass) => (
          <CollapsibleGroup key={schoolClass.id}>
            <div className="c-fabric-block">
              <CollapsibleHeader>
                <CollapsibleLabel
                  label="educationStageName"
                  value={schoolClass?.name}
                />
              </CollapsibleHeader>

              <CollapsibleContent>
                <div className="o-column o-row--gap-size_300 u-padding_500">
                  {schoolClass.subClasses?.map((subClass) => (
                    <BookSubClassItem
                      key={subClass.id}
                      subClass={subClass}
                      supple={supple}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </div>
          </CollapsibleGroup>
        ))}
      </CollapsibleList>
    </div>
  );
}

function UniformsClassItem({ setSupple, supple }) {
  const { t } = useTranslation("general");
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const [uniformsData, setUniformsData] = useState([]);
  const [loadingUniform, setLoadingUniform] = useState(false);
  const [errorGettingUniforms, setErrorGettingUniforms] = useState("");

  async function getAllUniforms() {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingUniform(true);
      const classId = searchParams.get("classId");
      const response = await axiosClient.get(
        "/studentSubClass/getAllForProductRulerItem",
        {
          params: {
            classId: classId,
            productRulerItemId: id,
          },
        }
      );

      if (response.data?.classes) {
        setSupple({
          schoolClass: { ...response.data.classes },
          productRulerItem: { ...response.data.productRulerItem },
          name: response.data.productRulerItem.size,
        });
        setUniformsData(response.data.classes);
      }
    } catch (err) {
      console.error("Failed to fetch uniforms:", err);
      setErrorGettingUniforms("gettingSupplieFailed");
    } finally {
      setLoadingUniform(false);
    }
  }

  useEffect(() => {
    getAllUniforms();
  }, []);

  if (loadingUniform) return <Loader size={LOADER_SIZES.MEDIUM} />;

  if (!loadingUniform && !errorGettingUniforms && uniformsData?.length === 0)
    return <InformationBox>{t("noDeliveredSupplesFound")}</InformationBox>;

  if (!loadingUniform && errorGettingUniforms)
    return <ErrorBox>{t(errorGettingUniforms)}</ErrorBox>;

  return (
    <CollapsibleList>
      {uniformsData?.map((uniform) => (
        <CollapsibleGroup key={uniform.id}>
          <div className="c-fabric-block">
            <CollapsibleHeader>
              <CollapsibleLabel
                label="educationStageName"
                value={uniform.name}
              />
            </CollapsibleHeader>
            <CollapsibleContent>
              <div className="o-column o-row--gap-size_300 u-padding_500">
                {uniform.subClasses.map((subClass) => (
                  <UniformSubClassItem
                    key={subClass.id}
                    subClass={subClass}
                    supple={supple}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </div>
        </CollapsibleGroup>
      ))}
    </CollapsibleList>
  );
}

function BookSubClassItem({ subClass, supple }) {
  const { t } = useTranslation("general");
  const [studentsData, setStudentsData] = useState([]);
  const [studentName, setStudentName] = useState("");
  const [studentCode, setStudentCode] = useState("");
  const studentNameValue = useDebounce(studentName);
  const studentCodeValue = useDebounce(studentCode);
  const [selectedDate, setSelectedDate] = useState({
    from: "",
    to: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [collapsed, setCollapsed] = useState(true);
  const [showEditSupplyModal, setShowEditSupplyModal] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [showAreYouSure, setShowAreYouSure] = useState(false);
  const [deleteSuppleLoading, setDeleteSuppleLoading] = useState(false);
  const [deleteSuppleError, setDeleteSuppleError] = useState("");
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsError, setStudentsError] = useState("");
  const [totalSold, setTotalSold] = useState(subClass.totalSold);

  const openEditSupplyModal = (supple) => {
    setSelectedSupply(supple);
    setShowEditSupplyModal(true);
  };

  const tableTitles = [
    {
      value: "studentId",
      keepOnMobile: true,
    },
    {
      value: "studentName",
      keepOnMobile: true,
    },
    {
      value: "theCount",
      keepOnMobile: true,
    },
    {
      value: "Date",
      keepOnMobile: true,
    },
    {
      value: "Actions",
      keepOnMobile: true,
    },
  ];

  const flattenedStudents = studentsData?.flatMap((student) =>
    student.sellDetails.map((detail) => ({
      id: student.id,
      studentCode: student.studentCode,
      fullName: student.fullName,
      phoneNumber1: student.phoneNumber1,
      phoneNumber2: student.phoneNumber2,
      sellId: detail.id,
      ...detail,
    }))
  );

  const tableRows = flattenedStudents?.map((student) => [
    {
      value: student.studentCode,
      type: TABLE_CELL_TYPES.TEXT,
      eclipseText: true,
      keepOnMobile: true,
    },
    {
      value: student.fullName,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: student.quantity,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: student.date,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: (
        <div className="o-row">
          <CustomButton
            variant="blank"
            icon={editIcon}
            iconSize={19}
            title={t("edit")}
            onClick={() => openEditSupplyModal(student)}
          />
          <CustomButton
            variant={"red"}
            icon={deleteIcon}
            iconSize={19}
            title={t("delete")}
            onClick={() => {
              setSelectedSupply(student);
              setShowAreYouSure(true);
            }}
          />
        </div>
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  async function getAllStudents() {
    setLoadingStudents(true);
    console.log("subClass", subClass);
    try {
      const response = await axiosClient.get(
        `/studentSubClass/getAllByClassId`,
        {
          params: {
            subClassId: subClass.id,
            productId: supple.product.id,
            studentCode: studentCodeValue,
            fullName: studentNameValue,
            fromDate: selectedDate.from,
            toDate: selectedDate.to,
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
          },
        }
      );
      setStudentsData(response.data.students);
      setPagination({
        currentPage: response.data.page,
        totalPages: response.data.totalPages,
        totalRecords: response.data.totalRecords,
      });
    } catch (error) {
      console.log("Error getting students", error);
      setStudentsError("errorGettingStudents");
    } finally {
      setLoadingStudents(false);
    }
  }

  async function handleDeleteSupple() {
    setDeleteSuppleLoading(true);
    try {
      const response = await axiosClient.delete(
        `/sell/${selectedSupply.sellId}`
      );
      getAllStudents();
      setShowAreYouSure(false);
      setTotalSold((prev) => prev - selectedSupply.quantity);
    } catch (error) {
      console.log("Error Deleting Supply", error);
      setDeleteSuppleError("errorDeleteSupplie");
    } finally {
      setDeleteSuppleLoading(false);
    }
  }

  useEffect(() => {
    if (collapsed) return;
    getAllStudents();
  }, [
    studentNameValue,
    studentCodeValue,
    selectedDate,
    pagination.currentPage,
    collapsed,
    selectedDate.from,
    selectedDate.to,
  ]);

  return (
    <CollapsibleCard
      collapsed={collapsed}
      toggleCollapsed={setCollapsed}
      title={
        <div className="o-row o-row--gap-size_300">
          <div className="o-row o-row--gap-size_200 o-column-row-switcher u-width_full-width">
            <span>
              {t("subclassName")}: {subClass?.name}
            </span>
            <span>
              {" "}
              - {t("total")}: {totalSold} {t("book")}
            </span>
          </div>
        </div>
      }
      body={
        <>
          <div className="o-column-row-switcher o-column--horizontal-align_space-between o-row--gap-size_300 u-margin--bottom_500 u-margin--top_500">
            <div className="o-row o-row--gap-size_300 o-column-row-switcher">
              <div className="c-search-bar">
                <section className="c-search-bar-label-input">
                  <CustomInput
                    title={t("studentId")}
                    value={studentCode}
                    onChange={(e) => setStudentCode(e.target.value)}
                    flex
                  />
                </section>
              </div>

              <div className="c-search-bar">
                <section className="c-search-bar-label-input">
                  <CustomInput
                    title={t("studentName")}
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    flex
                  />
                </section>
              </div>
            </div>

            <div className="o-row o-row--gap-size_300 o-column-row-switcher">
              <CustomInput
                type="date"
                title={t("from") + ": "}
                flex={false}
                value={selectedDate.from}
                onChange={(e) =>
                  setSelectedDate({ ...selectedDate, from: e.target.value })
                }
              />
              <CustomInput
                type="date"
                title={t("to") + ": "}
                flex={false}
                value={selectedDate.to}
                onChange={(e) =>
                  setSelectedDate({ ...selectedDate, to: e.target.value })
                }
              />
            </div>
          </div>
          {loadingStudents && <Loader size={LOADER_SIZES.MEDIUM} />}
          {studentsError && <ErrorBox>{t(studentsError)}</ErrorBox>}
          {!loadingStudents && !studentsError && !studentsData?.length > 0 && (
            <InformationBox>{t("noStudents")}</InformationBox>
          )}
          {!loadingStudents && !studentsError && studentsData?.length > 0 && (
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
              mobileEffect={true}
            />
          )}
          {showEditSupplyModal && (
            <EditSupplyModal
              studentSupply={selectedSupply}
              onClose={() => {
                setShowEditSupplyModal(false);
                getAllStudents();
              }}
              onUpdate={({ quantity }) => {
                setShowEditSupplyModal(false);
                getAllStudents();
                setTotalSold((prev) => {
                  return prev - selectedSupply.quantity + quantity;
                });
              }}
            />
          )}
          {showAreYouSure && (
            <AreYouSureModal
              error={t(deleteSuppleError)}
              loading={deleteSuppleLoading}
              confirmTitle={"delete"}
              onClose={() => setShowAreYouSure(false)}
              onConfirm={handleDeleteSupple}
              confirmIconType="whiteCheck"

            />
          )}
        </>
      }
    />
  );
}

function UniformSubClassItem({ subClass, supple }) {
  const { t } = useTranslation("general");
  const [studentsData, setStudentsData] = useState([]);
  const [studentName, setStudentName] = useState("");
  const [studentCode, setStudentCode] = useState("");
  const studentNameValue = useDebounce(studentName);
  const studentCodeValue = useDebounce(studentCode);
  const [selectedDate, setSelectedDate] = useState({
    from: "",
    to: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [collapsed, setCollapsed] = useState(true);
  const [showEditSupplyModal, setShowEditSupplyModal] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [deleteSuppleError, setDeleteSuppleError] = useState(null);
  const [deleteSuppleLoading, setDeleteSuppleLoading] = useState(false);
  const [showAreYouSure, setShowAreYouSure] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsError, setStudentsError] = useState("");
  const [totalSold, setTotalSold] = useState(subClass.totalSold);

  const openEditSupplyModal = (supple) => {
    setSelectedSupply(supple);
    setShowEditSupplyModal(true);
  };

  const flattenedStudents = studentsData?.flatMap((student) =>
    student.sellDetails.map((detail) => ({
      id: student.id,
      studentCode: student.studentCode,
      fullName: student.fullName,
      phoneNumber1: student.phoneNumber1,
      phoneNumber2: student.phoneNumber2,
      ...detail,
    }))
  );

  const tableTitles = [
    {
      value: "studentId",
      keepOnMobile: true,
    },
    {
      value: "studentName",
      keepOnMobile: true,
    },
    {
      value: "theCount",
      keepOnMobile: true,
    },
    {
      value: "Date",
      keepOnMobile: true,
    },
    {
      value: "Actions",
      keepOnMobile: true,
    },
  ];

  const tableRows = flattenedStudents?.map((student) => [
    {
      value: student.studentCode,
      type: TABLE_CELL_TYPES.TEXT,
      eclipseText: true,
      keepOnMobile: true,
    },
    {
      value: student.fullName,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: student.quantity,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: student.date,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: (
        <div className="o-row">
          <CustomButton
            variant="blank"
            icon={editIcon}
            iconSize={19}
            title={t("edit")}
            onClick={() => openEditSupplyModal(student)}
          />
          <CustomButton
            variant="red"
            icon={deleteIcon}
            iconSize={19}
            title={t("delete")}
            onClick={() => {
              setShowAreYouSure(true);
              setSelectedSupply(student);
            }}
          />
        </div>
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  async function getAllStudents() {
    setLoadingStudents(true);
    try {
      const response = await axiosClient.get(
        `/studentSubClass/getAllForByClassIdForProductRulerItem`,
        {
          params: {
            subClassId: subClass.id,
            productRulerItemId: supple.productRulerItem.id,
            studentCode: studentCodeValue,
            fullName: studentNameValue,
            fromDate: selectedDate.from,
            toDate: selectedDate.to,
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
          },
        }
      );
      setStudentsData(response.data.students);
      setPagination({
        currentPage: response.data.page,
        totalPages: response.data.totalPages,
        totalRecords: response.data.totalRecords,
      });
    } catch (error) {
      console.log("Error getting students", error);
      setStudentsError("errorGettingStudents");
    } finally {
      setLoadingStudents(false);
    }
  }

  useEffect(() => {
    if (collapsed) return;

    getAllStudents();
  }, [
    studentNameValue,
    studentCodeValue,
    selectedDate,
    pagination.currentPage,
    collapsed,
    selectedDate.from,
    selectedDate.to,
  ]);

  async function handleDeleteSupple() {
    setDeleteSuppleLoading(true);
    try {
      const response = await axiosClient.delete(
        `/sell/${selectedSupply.sellId}`
      );
      getAllStudents();
      setShowAreYouSure(false);
      setTotalSold((prev) => prev - selectedSupply.quantity);
    } catch (error) {
      console.log("Error Deleting Supply", error);
      setDeleteSuppleError("errorDeleteSupplie");
    } finally {
      setDeleteSuppleLoading(false);
    }
  }

  return (
    <CollapsibleCard
      collapsed={collapsed}
      toggleCollapsed={setCollapsed}
      title={
        <div className="o-row o-row--gap-size_300">
          <div className="o-row o-row--gap-size_200 o-column-row-switcher u-width_full-width">
            <span>
              {t("subclassName")}: {subClass?.name}
            </span>
            <span>
              {" "}
              - {t("total")}: {totalSold} {t("piece")}
            </span>
          </div>
        </div>
      }
      body={
        <>
          <div className="o-column-row-switcher o-column--horizontal-align_space-between o-row--gap-size_300 u-margin--bottom_500 u-margin--top_500">
            <div className="o-row o-row--gap-size_300 o-column-row-switcher">
              <div className="c-search-bar">
                <section className="c-search-bar-label-input">
                  <CustomInput
                    title={t("studentId")}
                    value={studentCode}
                    onChange={(e) => setStudentCode(e.target.value)}
                    flex
                  />
                </section>
              </div>

              <div className="c-search-bar">
                <section className="c-search-bar-label-input">
                  <CustomInput
                    title={t("studentName")}
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    flex
                  />
                </section>
              </div>
            </div>

            <div className="o-row o-row--gap-size_300 o-column-row-switcher">
              <CustomInput
                type="date"
                title={t("from") + ": "}
                flex={false}
                value={selectedDate.from}
                onChange={(e) =>
                  setSelectedDate({ ...selectedDate, from: e.target.value })
                }
              />
              <CustomInput
                type="date"
                title={t("to") + ": "}
                flex={false}
                value={selectedDate.to}
                onChange={(e) =>
                  setSelectedDate({ ...selectedDate, to: e.target.value })
                }
              />
            </div>
          </div>
          {loadingStudents && <Loader size={LOADER_SIZES.MEDIUM} /> }
          {studentsError && <ErrorBox>{t(studentsError)}</ErrorBox>}
          {!loadingStudents && !studentsError && !studentsData?.length > 0 && (
            <InformationBox>{t("noStudents")}</InformationBox>
          )}
          {!loadingStudents && !studentsError && studentsData?.length > 0 && (
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
              mobileEffect={true}
            />
          )}
          {showEditSupplyModal && (
            <EditSupplyModal
              studentSupply={selectedSupply}
              onClose={() => setShowEditSupplyModal(false)}
              onUpdate={({ quantity }) => {
                setShowEditSupplyModal(false);
                getAllStudents();
                setTotalSold((prev) => {
                  return prev - selectedSupply.quantity + quantity;
                });
              }}
            />
          )}

          {showAreYouSure && (
            <AreYouSureModal
              error={t(deleteSuppleError)}
              loading={deleteSuppleLoading}
              confirmTitle={"delete"}
              onClose={() => setShowAreYouSure(false)}
              onConfirm={handleDeleteSupple}
              confirmIconType="whiteCheck"

            />
          )}
        </>
      }
    />
  );
}

function EditSupplyModal({ studentSupply, onClose, onUpdate }) {
  const { t } = useTranslation("general");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [supple, setSupple] = useState(studentSupply);

  async function handleUpdateSupply() {
    try {
      setIsSubmitting(true);
      await axiosClient.put(`sell/${supple.sellId}`, {
        ...supple,
        quantity: Number(supple.quantity),
      });
      onUpdate?.({
        quantity: Number(supple.quantity),
        sellId: supple.sellId,
      });
    } catch (error) {
      console.error("Error updating supple:", error);
      setErrorMessage(t("editSupplieError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleInputChange(filed, value) {
    if (filed === "quantity") {
      if (value <= 0) return;
    }
    setSupple({ ...supple, [filed]: value });
  }

  return (
    <Modal
      title={t("editSupplie")}
      error={errorMessage}
      onClose={onClose}
      modalSize="c-modal--small"
      footer={
        <>
          {isSubmitting ? <Loader /> : null}
          <section className="edit-user-actions">
            <CustomButton
              title={t("confirm")}
              onClick={handleUpdateSupply}
              icon={saveIcon}
              iconSize={16}
              disabled={isSubmitting}
            />
            <CustomButton
              title={t("cancel")}
              onClick={onClose}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </section>
        </>
      }
    >
      <form>
        <div>
          {errorMessage && (
            <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
              <ErrorBox>{errorMessage}</ErrorBox>
            </div>
          )}
          <div className="o-column o-row--gap-size_400">
            <div className="o-row o-row--gap-size_300">
              <span className="t-bold_semi-bold">{t("studentName")}:</span>
              <span className="t-bold_semi-bold">{supple.fullName}</span>
            </div>

            <div className="o-row o-row--gap-size_300">
              <span className="t-bold_semi-bold">{t("studentId")}:</span>
              <span className="t-bold_semi-bold">{supple.studentCode}</span>
            </div>
            <div className="o-row o-row--gap-size_300">
              <CustomInput
                title={t("theCount")}
                value={supple.quantity}
                onChange={(e) => handleInputChange("quantity", e.target.value)}
                type="number"
                flex
              />
            </div>

            <div className="o-row o-row--gap-size_300">
              <CustomInput
                title={t("Date")}
                value={formatDate(supple.date)}
                onChange={(e) => handleInputChange("date", e.target.value)}
                type="date"
                flex
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default DeliveredSuppliesItemPage;
